import { useState, useRef, useEffect, forwardRef } from 'react'
import { DraggableProvidedDragHandleProps, DraggableProvidedDraggableProps } from '@hello-pangea/dnd'
import { Rock } from '../../types'
import RockCheckInPopover from './RockCheckInPopover'
import RockPercentSlider from './RockPercentSlider'
import UserAvatar from '../shared/UserAvatar'
import RowActionsMenu from '../shared/RowActionsMenu'
import { queueRockUpdate } from '../../utils/rockSaveQueue'

interface Props {
  rock: Rock
  teamId: number
  isBacklog?: boolean
  showOwner?: boolean
  onViewDetail: (rock: Rock) => void
  dragHandleProps?: DraggableProvidedDragHandleProps | null
  rowProps?: DraggableProvidedDraggableProps
  isDragging?: boolean
  /** Links check-ins recorded from this row to the meeting they happened in. */
  meetingId?: number
  /** Rock Review keeps the latest check-in note visible on the board. */
  showCheckInNote?: boolean
  /**
   * Task #798: opt-in per-row actions menu ("Create Topic"). Only the
   * in-meeting Rock Review table asks for it — never the standalone Rocks
   * page or the backlog view.
   */
  showRowMenu?: boolean
  /** Open state lives above the rows so opening one menu closes any other. */
  menuOpen?: boolean
  onToggleMenu?: () => void
  onCloseMenu?: () => void
  onCreateTopic?: (rock: Rock) => void
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

const SAVED_CUE_MS = 2000

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffDays < 1) return 'Today'
  if (diffDays === 1) return '1d ago'
  if (diffDays < 7) return `${diffDays}d ago`
  const diffWeeks = Math.floor(diffDays / 7)
  return `${diffWeeks}w ago`
}

function dateInputValue(dateStr: string | null): string {
  if (!dateStr) return ''
  return dateStr.slice(0, 10)
}

function formatDueDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return '—'
  const date = new Date(y, m - 1, d)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const RockRow = forwardRef<HTMLTableRowElement, Props>(function RockRow(
  {
    rock, teamId, isBacklog, showOwner, onViewDetail, dragHandleProps, rowProps, isDragging,
    meetingId, showCheckInNote, showRowMenu, menuOpen, onToggleMenu, onCloseMenu, onCreateTopic,
  },
  ref
) {
  const [showPopover, setShowPopover] = useState(false)
  // Round: stored percent can be fractional (computed from a target); the
  // slider works in whole numbers and the backend requires 0.5 increments.
  const [percent, setPercent] = useState<number>(Math.round(rock.percent_complete ?? 0))
  const [editingDue, setEditingDue] = useState(false)
  const [due, setDue] = useState<string>(dateInputValue(rock.due_date))
  const [status, setStatus] = useState<SaveStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const statusRef = useRef<HTMLTableCellElement>(null)
  const savedTimer = useRef<number | undefined>(undefined)
  // Fields with a save in flight. The meeting page re-serializes every prop on
  // its 10s heartbeat, so incoming props may still carry the pre-save value —
  // only fields that aren't mid-edit adopt what the server sends.
  const savingFieldsRef = useRef<Set<string>>(new Set())

  const draggable = !!dragHandleProps
  const canEdit = rock.can_edit !== false

  useEffect(() => () => {
    if (savedTimer.current) window.clearTimeout(savedTimer.current)
  }, [])

  useEffect(() => {
    if (savingFieldsRef.current.has('percent_complete')) return
    setPercent(Math.round(rock.percent_complete ?? 0))
  }, [rock.percent_complete])

  useEffect(() => {
    if (editingDue || savingFieldsRef.current.has('due_date')) return
    setDue(dateInputValue(rock.due_date))
  }, [rock.due_date, editingDue])

  const flashSaved = () => {
    setStatus('saved')
    if (savedTimer.current) window.clearTimeout(savedTimer.current)
    savedTimer.current = window.setTimeout(() => {
      setStatus(current => (current === 'saved' ? 'idle' : current))
    }, SAVED_CUE_MS)
  }

  const saveField = (field: string, value: string | number | null, revert: () => void) => {
    savingFieldsRef.current.add(field)
    setErrorMessage(null)
    setStatus('saving')
    queueRockUpdate(teamId, rock.id, { [field]: value }, {
      onSuccess: () => {
        savingFieldsRef.current.delete(field)
        flashSaved()
      },
      onError: (_errors, message) => {
        savingFieldsRef.current.delete(field)
        revert()
        setErrorMessage(message)
        setStatus('error')
      },
    })
  }

  const commitPercent = (v: number) => {
    if (!canEdit) return
    if (v === Math.round(rock.percent_complete ?? 0)) return
    saveField('percent_complete', v, () => setPercent(Math.round(rock.percent_complete ?? 0)))
  }

  // Never save inside onChange: a native date input fires `change` as soon as
  // every segment holds any value, so typing a year emits bogus intermediate
  // dates like 0002-07-20.
  const commitDueDate = () => {
    setEditingDue(false)
    const next = due || null
    if (dateInputValue(rock.due_date) === (next ?? '')) return
    saveField('due_date', next, () => setDue(dateInputValue(rock.due_date)))
  }

  const cancelDueEdit = () => {
    setDue(dateInputValue(rock.due_date))
    setEditingDue(false)
  }

  return (
    <tr
      ref={ref}
      {...(rowProps || {})}
      className={`border-b border-stone/10 transition-colors ${isDragging ? 'bg-white shadow-lg' : 'hover:bg-[#F5F5F5]/20'}`}
      draggable={false}
      onDragStart={e => e.preventDefault()}
    >
      {draggable && (
        <td
          className="w-8 px-1 text-center text-text-muted hover:text-pinnacle-red cursor-grab active:cursor-grabbing"
          {...dragHandleProps}
          aria-label="Drag to reorder"
          title="Drag to reorder"
        >
          <svg className="w-5 h-5 inline-block" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 4a1 1 0 110 2 1 1 0 010-2zm0 5a1 1 0 110 2 1 1 0 010-2zm0 5a1 1 0 110 2 1 1 0 010-2zm6-10a1 1 0 110 2 1 1 0 010-2zm0 5a1 1 0 110 2 1 1 0 010-2zm0 5a1 1 0 110 2 1 1 0 010-2z" />
          </svg>
        </td>
      )}
      <td className="py-3 px-4">
        <button
          onClick={() => onViewDetail(rock)}
          className="text-sm font-medium text-text-primary hover:text-pinnacle-red transition-colors text-left"
        >
          {rock.title}
        </button>
        {rock.is_company_rock && (
          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-sm bg-charcoal/10 text-text-secondary">Co</span>
        )}
        {showCheckInNote && rock.last_check_in_note && (
          <p className="mt-1 text-sm text-text-secondary line-clamp-2">{rock.last_check_in_note}</p>
        )}
        {status === 'saving' && (
          <p role="status" className="mt-1 text-xs text-text-muted">Saving…</p>
        )}
        {status === 'saved' && (
          <p role="status" className="mt-1 text-xs text-text-muted">Saved</p>
        )}
        {status === 'error' && (
          <p role="alert" className="mt-1 text-xs text-pinnacle-red">{errorMessage}</p>
        )}
      </td>

      {showOwner && (
        <td className="py-3 px-3">
          {rock.owner ? (
            <div className="flex items-center gap-2 min-w-0">
              <UserAvatar user={rock.owner} size="xs" />
              <span className="text-sm text-text-primary truncate max-w-[140px]" title={rock.owner.full_name}>
                {rock.owner.full_name}
              </span>
            </div>
          ) : (
            <span className="text-sm text-text-muted">—</span>
          )}
        </td>
      )}

      <td className="py-3 px-3 text-center">
        {rock.milestones_total_count > 0 ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-sm bg-charcoal/10 text-text-primary">
            {rock.milestones_completed_count}/{rock.milestones_total_count}
          </span>
        ) : (
          <span className="text-sm text-text-muted">—</span>
        )}
      </td>

      <td className="py-3 px-3">
        {canEdit ? (
          editingDue ? (
            <input
              type="date"
              autoFocus
              value={due}
              onChange={(e) => setDue(e.target.value)}
              onBlur={commitDueDate}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  commitDueDate()
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  e.stopPropagation()
                  cancelDueEdit()
                }
              }}
              aria-label={`Due date for ${rock.title}`}
              className="text-sm text-text-primary border border-stone/30 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-pinnacle-red"
            />
          ) : (
            <button
              type="button"
              onClick={() => setEditingDue(true)}
              className="text-sm text-text-primary hover:text-pinnacle-red transition-colors"
              aria-label={`Edit due date for ${rock.title}`}
            >
              {formatDueDate(due || null)}
            </button>
          )
        ) : (
          <span className="text-sm text-text-primary">{formatDueDate(rock.due_date)}</span>
        )}
      </td>

      {!isBacklog && (
        <td className="py-3 px-3" ref={statusRef}>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-[100px]">
              <RockPercentSlider
                value={percent}
                onChange={setPercent}
                onCommit={commitPercent}
                disabled={!canEdit}
                size="sm"
                ariaLabel={`Percent complete for ${rock.title}`}
              />
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={() => setShowPopover(!showPopover)}
                className="text-xs text-pinnacle-red hover:underline whitespace-nowrap"
                aria-label={`Quick check-in for ${rock.title}`}
              >
                Check in
              </button>
            )}
            {/* Fixed-width slot so the column keeps its size whether or not a
                given row ends up rendering a trigger. */}
            {showRowMenu && (
              <div className="w-7 shrink-0">
                <RowActionsMenu
                  itemName={rock.title}
                  open={menuOpen === true}
                  onToggle={() => onToggleMenu?.()}
                  onClose={() => onCloseMenu?.()}
                  onCreateTopic={() => onCreateTopic?.(rock)}
                />
              </div>
            )}
          </div>
          {showPopover && (
            <RockCheckInPopover
              rock={rock}
              teamId={teamId}
              meetingId={meetingId}
              onClose={() => setShowPopover(false)}
              anchorRef={statusRef}
            />
          )}
        </td>
      )}
      {!isBacklog && (
        <td className="py-3 px-3">
          <span className="text-sm text-text-secondary">{timeAgo(rock.last_check_in_at)}</span>
        </td>
      )}
    </tr>
  )
})

export default RockRow
